import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useExcJpmamV1JpmamJobQueryServiceJpmamJobGet } from '@interxfi/api-client/generated/hooks/exc-jpmam-v1-jpmam-job-query-service-jpmam-job-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcJpmamV1JpmamJobState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { TableSectionHeader } from '../../../../../../../../../../components/table/table-section-header';
import { JpmamJobEntityMenu } from '../../../../../../../../../../features/vendor/jpmam/job/jpmam-job-entity-menu';
import { JpmamJobEventSummary } from '../../../../../../../../../../features/vendor/jpmam/job/jpmam-job-event';
import { getJpmamJobStateListItems } from '../../../../../../../../../../features/vendor/jpmam/job/jpmam-job-list-items';
import { getJpmamJobDetailPath } from '../../../../../../../../../../features/vendor/jpmam/job/jpmam-job-routing';
import { JpmamJobStatusPill } from '../../../../../../../../../../features/vendor/jpmam/job/jpmam-job-status-pill';
import { JpmamTasksTable } from '../../../../../../../../../../features/vendor/jpmam/task/jpmam-tasks-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/jpm/job/_job/$jobId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `jpmam-job-${params.jobId}`,
        label: 'Job',
        to: getJpmamJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('JPMAM Job'),
  component: JpmamJobDetail,
});

function getListItems(data: ExcJpmamV1JpmamJobState | undefined) {
  const items = getJpmamJobStateListItems(data);

  return {
    general: {
      left: [
        items.data.operationType,
        ...match(items.data.spec)
          .with({ orderStatusNotify: P.not(P.nullish) }, ({ orderStatusNotify }) => [
            orderStatusNotify.value.netOrderId,
            orderStatusNotify.value.groupingId,
            orderStatusNotify.value.targetNetOrderStatusPill,
            orderStatusNotify.value.totalOrderIds,
            orderStatusNotify.value.statusData.settlementDate,
            orderStatusNotify.value.statusData.settlementTime,
            ...(orderStatusNotify.value.statusData.reason ? [orderStatusNotify.value.statusData.reason] : []),
          ])
          .otherwise(() => STABLE_EMPTY_ARRAY_REF),
      ] as const satisfies ReadonlyListItem[],
      right: [items.jpmamJobId, items.metadata.createdAt, items.metadata.updatedAt] as const satisfies ReadonlyListItem[],
    },
    metrics: [
      items.data.metrics.total,
      items.data.metrics.succeeded,
      items.data.metrics.pending,
      items.data.metrics.errored,
      items.data.metrics.failed,
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

function TasksTableHeader(props: CustomTableHeaderProps) {
  return <TableSectionHeader {...props} title="Tasks" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load JPMAM job', 'view this JPMAM job');

function JpmamJobDetail() {
  const { jobId } = Route.useParams();
  const request = { jpmamJobId: jobId } as const;
  const { data, error, isPending } = useExcJpmamV1JpmamJobQueryServiceJpmamJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.jpmamJob), [data?.jpmamJob]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <JpmamJobStatusPill isPending={showPending} size="large" status={data?.jpmamJob?.status} />
          </HorizontalStack>

          <JpmamJobEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <JpmamJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Metrics</Typography.Level3>
        </VerticalStack>

        <AppReadonlyList isPending={showPending} items={listItems.metrics} />
      </PageLayout.Section>

      <PageLayout.Section>
        <JpmamTasksTable HeaderActions={TasksTableHeader} jobId={jobId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
